import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FilePreview } from '@alefeducation/file-preview';
import {
  FileAudio,
  FileImage,
  Pdf,
  Video,
} from '@alefeducation/ui-elements-library-icons';
import {
  getAttachmentGeneralFileType,
  getAttachmentRenderableFileType,
  getDownloadableFileLink,
  getFileLink,
  getPreviewFileURL,
  shouldCheckForFileExistance,
} from '../../AssignmentSubmission/attachment.utils';
import { ModalContent } from '../../Assignment/style';
import HelpButton from '../HelpButton/HelpButton';
import Modal from '../Modal/Modal';
import Close from '../Icons/Close';

const PopupFileRenderer = styled(FilePreview)`
  & img {
    max-height: calc(90vh - 220px);
  }
`;

export const attachmentTypeIconRenderer = (
  action,
  attachment,
  overwrittenAttachmentName,
  withLabel,
  color,
) => {
  const { t } = useTranslation();
  const fileType = attachment && getAttachmentGeneralFileType(attachment);
  const downloadbleAttachment =
    attachment &&
    (fileType === 'document'
      ? getDownloadableFileLink(
          getFileLink(attachment),
          attachment.fileName,
          `_${overwrittenAttachmentName.replace(/ /g, '_')}`,
        )
      : getDownloadableFileLink(
          getPreviewFileURL(attachment),
          attachment.fileName,
        ));
  return {
    document: (
      <>
        <a
          data-tip={t('Download File')}
          href={downloadbleAttachment}
          style={{ display: 'flex', alignItems: 'flex-start' }}
        >
          <Pdf data-testid="attachment-type-icon" color={color} fontSize={18} />
          {withLabel && <span>{t('Download File')}</span>}
        </a>
      </>
    ),
    image: (
      <>
        <FileImage
          data-tip={t('View file')}
          onClick={e => {
            e.stopPropagation();
            action(true);
          }}
          data-testid="attachment-type-icon"
          color={color}
          fontSize={18}
        />
        {withLabel && (
          <span
            onClick={e => {
              e.stopPropagation();
              action(true);
            }}
          >
            {t('View file')}
          </span>
        )}
      </>
    ),
    audio: (
      <>
        <FileAudio
          data-tip={t('View file')}
          onClick={e => {
            e.stopPropagation();
            action(true);
          }}
          data-testid="attachment-type-icon"
          color={color}
          fontSize={18}
        />
        {withLabel && (
          <span
            onClick={e => {
              e.stopPropagation();
              action(true);
            }}
          >
            {t('View file')}
          </span>
        )}
      </>
    ),
    video: (
      <>
        <Video
          data-tip={t('View file')}
          onClick={e => {
            e.stopPropagation();
            action(true);
          }}
          data-testid="attachment-type-icon"
          color={color}
          fontSize={18}
        />
        {withLabel && (
          <span
            onClick={e => {
              e.stopPropagation();
              action(true);
            }}
          >
            {t('View file')}
          </span>
        )}
      </>
    ),
  }[fileType];
};

export default function AttachmentPreviewModal(props) {
  const {
    title,
    loading,
    tooltipContent,
    buttonTextCancel,
    testId,
    renderProp,
    attachment,
    openByDefault,
  } = props;
  const [previewModal, setPreviewModal] = useState(openByDefault);
  const { t, dir } = useTranslation();

  return (
    <>
      {renderProp(setPreviewModal)}
      {previewModal && (
        <Modal
          onCloseModal={() => setPreviewModal(false)}
          testId={testId}
          dir={dir}
        >
          <div
            className={`attachment-preview-modal ${
              loading ? 'is-loading' : ''
            }`}
          >
            {tooltipContent && (
              <div
                data-testid="attachment-preview-modal-help"
                className="attachment-preview-modal__help"
              >
                <HelpButton tooltipContent={t(tooltipContent)} />
              </div>
            )}
            <button
              data-testid="attachment-preview-modal-closeButton"
              className="attachment-preview-modal__close-button"
              onClick={() => setPreviewModal(false)}
            >
              <Close size={25} color="#485465" />
            </button>
            <h1 className="attachment-preview-modal__title usprivacy">
              {title}
            </h1>
            <ModalContent>
              <PopupFileRenderer
                checkForFileExistanceFirst={shouldCheckForFileExistance(
                  attachment,
                )}
                fileUrl={getPreviewFileURL(attachment)}
                fileType={getAttachmentRenderableFileType(attachment)}
              />
            </ModalContent>
            <div className="attachment-preview-modal__buttons">
              {buttonTextCancel && (
                <button
                  data-testid="attachment-preview-modal-cancelButton"
                  className="attachment-preview-modal__button attachment-preview-modal__button--cancel"
                  onClick={() => setPreviewModal(false)}
                >
                  {t(buttonTextCancel)}
                </button>
              )}
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

AttachmentPreviewModal.propTypes = {
  loading: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  tooltipContent: PropTypes.node,
  buttonTextCancel: PropTypes.string.isRequired,
  renderProp: PropTypes.func.isRequired,
  attachment: PropTypes.shape({
    fileName: PropTypes.string.isRequired,
    path: PropTypes.string.isRequired,
    fileId: PropTypes.string,
  }).isRequired,
  testId: PropTypes.string,
  openByDefault: PropTypes.bool,
  color: PropTypes.string,
};

AttachmentPreviewModal.defaultProps = {
  tooltipContent: null,
  testId: '',
  openByDefault: false,
  color: '#4DC9C5',
};
